import { useCallback, useRef, useState } from "react";

import { parseAsString, useQueryState } from "@/common/hooks/useQueryState";
import { RateOutput } from "@/common/types/trpc";

const AUCTION_ITEM_PARAM = "auctionItem";
const auctionItemQueryState = parseAsString.withOptions({ history: "replace" });

export function useAuctionDrawerState(rates: RateOutput[]) {
  const [auctionItemId, setAuctionItemId] = useQueryState(AUCTION_ITEM_PARAM, auctionItemQueryState);

  const [drawerState, setDrawerState] = useState<{ open: boolean; selectedRateIndex: number }>(() => {
    if (auctionItemId && rates.length > 0) {
      const index = rates.findIndex((r) => r.id === auctionItemId);
      if (index !== -1) {
        return { open: true, selectedRateIndex: index };
      }
    }
    return { open: false, selectedRateIndex: 0 };
  });

  const ratesRef = useRef(rates);
  ratesRef.current = rates;
  const selectedRateIndexRef = useRef(drawerState.selectedRateIndex);
  selectedRateIndexRef.current = drawerState.selectedRateIndex;

  const selectedRate = rates[drawerState.selectedRateIndex] as RateOutput | undefined;

  const openDrawerForRate = useCallback(
    (rateId: string) => {
      const index = ratesRef.current.findIndex((r) => r.id === rateId);
      if (index !== -1) {
        setDrawerState({ open: true, selectedRateIndex: index });
        setAuctionItemId(rateId);
      }
    },
    [setAuctionItemId]
  );

  const closeDrawer = useCallback(() => {
    setDrawerState((prev) => ({ ...prev, open: false }));
    setAuctionItemId(null);
  }, [setAuctionItemId]);

  const goToNext = useCallback(() => {
    const currentRates = ratesRef.current;
    const nextIndex = Math.min(selectedRateIndexRef.current + 1, currentRates.length - 1);
    const nextRate = currentRates[nextIndex];
    if (nextRate) {
      setAuctionItemId(nextRate.id);
    }
    setDrawerState((prev) => ({ ...prev, selectedRateIndex: nextIndex }));
  }, [setAuctionItemId]);

  const goToPrevious = useCallback(() => {
    const currentRates = ratesRef.current;
    const prevIndex = Math.max(selectedRateIndexRef.current - 1, 0);
    const prevRate = currentRates[prevIndex];
    if (prevRate) {
      setAuctionItemId(prevRate.id);
    }
    setDrawerState((prev) => ({ ...prev, selectedRateIndex: prevIndex }));
  }, [setAuctionItemId]);

  return {
    isOpen: drawerState.open,
    selectedRate,
    openDrawerForRate,
    closeDrawer,
    goToNext,
    goToPrevious,
    hasNext: drawerState.selectedRateIndex < rates.length - 1,
    hasPrevious: drawerState.selectedRateIndex > 0,
  };
}
